import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  Timestamp,
  orderBy,
  limit,
  writeBatch,
  doc,
  deleteDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../hooks/useAuth';
import { AnalysisScan, AIPlatform, ScanTask } from '../../types/tool1-enhanced';
import {
  BarChart3,
  Play,
  Clock,
  CheckCircle,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  Plus,
  Eye,
  Bell,
} from 'lucide-react';
import { Button } from '../shared';

/**
 * Tool 1 - Analysis
 * AI scanning and gap analysis dashboard
 */
export function Analysis() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [scans, setScans] = useState<AnalysisScan[]>([]);
  const [selectedScan, setSelectedScan] = useState<AnalysisScan | null>(null);
  const [showNewScan, setShowNewScan] = useState(false);

  // New scan configuration
  const [scanType, setScanType] = useState<'ai_platform' | 'website_compliance'>('ai_platform');
  const [newScanName, setNewScanName] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<AIPlatform[]>([]);
  const [customQueries, setCustomQueries] = useState<string[]>(['']);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [brandName, setBrandName] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('custom');
  const [availablePlatforms, setAvailablePlatforms] = useState<AIPlatform[]>(['gemini']);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [scanProgress, setScanProgress] = useState<{
    show: boolean;
    current: number;
    total: number;
    startTime: number;
  }>({ show: false, current: 0, total: 0, startTime: 0 });
  const [queryResults, setQueryResults] = useState<any[]>([]);

  // Query templates
  const queryTemplates = {
    custom: {
      name: 'Custom Queries',
      description: 'Enter your own test queries',
      queries: [''],
    },
    brand_awareness: {
      name: 'Brand Awareness',
      description: 'Test if AI platforms know about your brand',
      queries: [
        'What is [BRAND]?',
        'Tell me about [BRAND]',
        'Who is [BRAND]?',
        'What does [BRAND] do?',
      ],
    },
    product_features: {
      name: 'Product & Features',
      description: 'Test product knowledge and feature awareness',
      queries: [
        'What does [BRAND] offer?',
        'What are [BRAND]\'s main features?',
        'How does [BRAND] work?',
        'What problems does [BRAND] solve?',
      ],
    },
    comparison: {
      name: 'Competitive Comparison',
      description: 'See if you appear in competitive queries',
      queries: [
        'Best tools for [CATEGORY]',
        '[BRAND] vs competitors',
        'Alternatives to [COMPETITOR]',
        'Top [CATEGORY] solutions in 2026',
      ],
    },
    use_case: {
      name: 'Use Case Queries',
      description: 'Test if AI recommends you for relevant use cases',
      queries: [
        'How to [SOLVE_PROBLEM]',
        'What tool should I use for [USE_CASE]?',
        'I need to [TASK], what should I use?',
        'Best way to [GOAL]',
      ],
    },
    pricing: {
      name: 'Pricing & Plans',
      description: 'Test pricing information accuracy',
      queries: [
        'How much does [BRAND] cost?',
        '[BRAND] pricing',
        '[BRAND] plans and pricing',
        'Is [BRAND] free?',
      ],
    },
    comprehensive: {
      name: 'Comprehensive Scan',
      description: 'All query types (12 queries)',
      queries: [
        'What is [BRAND]?',
        'Tell me about [BRAND]',
        'What does [BRAND] offer?',
        'How does [BRAND] work?',
        'Best tools for [CATEGORY]',
        '[BRAND] vs competitors',
        'How much does [BRAND] cost?',
        '[BRAND] pricing',
        'How to [SOLVE_PROBLEM]',
        'What are [BRAND]\'s main features?',
        'Alternatives to [COMPETITOR]',
        'Is [BRAND] any good?',
      ],
    },
  };

  const applyTemplate = (templateKey: string, customBrandName?: string) => {
    setSelectedTemplate(templateKey);
    const template = queryTemplates[templateKey as keyof typeof queryTemplates];
    if (template) {
      // Use provided brand name or fall back to state
      const brand = customBrandName !== undefined ? customBrandName : brandName;

      // Replace placeholders with actual brand name
      const queries = template.queries.map(q => {
        let query = q;
        if (brand) {
          query = query.replace(/\[BRAND\]/g, brand);
        }
        // Keep other placeholders like [CATEGORY], [COMPETITOR], etc. for user to customize
        return query;
      });
      setCustomQueries(queries);
    }
  };

  useEffect(() => {
    if (user) {
      loadScans();
      loadAvailablePlatforms();
    }
  }, [user]);

  // Update timer every second while scan is running
  useEffect(() => {
    if (!scanProgress.show) return;

    const interval = setInterval(() => {
      // Force re-render to update timer
      setScanProgress(prev => ({ ...prev }));
    }, 1000);

    return () => clearInterval(interval);
  }, [scanProgress.show]);

  const loadAvailablePlatforms = async () => {
    if (!user) return;

    setLoadingPlatforms(true);
    try {
      const getAvailablePlatformsFn = httpsCallable(functions, 'getAvailablePlatforms');
      const result = await getAvailablePlatformsFn({});
      const data = result.data as any;

      if (data.success && data.platforms) {
        setAvailablePlatforms(data.platforms as AIPlatform[]);
        // Auto-select Gemini as default if available
        if (data.platforms.includes('gemini')) {
          setSelectedPlatforms(['gemini']);
        } else if (data.platforms.length > 0) {
          setSelectedPlatforms([data.platforms[0]]);
        }
      }
    } catch (error) {
      console.error('Error loading available platforms:', error);
      // Fallback to Gemini only
      setAvailablePlatforms(['gemini']);
      setSelectedPlatforms(['gemini']);
    } finally {
      setLoadingPlatforms(false);
    }
  };

  const loadScans = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const q = query(
        collection(db, 'analysisScans'),
        where('userId', '==', user.uid),
        orderBy('scan_date', 'desc'),
        limit(20)
      );

      const querySnapshot = await getDocs(q);
      const loadedScans: AnalysisScan[] = [];

      querySnapshot.forEach((docSnap) => {
        loadedScans.push({ ...docSnap.data(), scan_id: docSnap.id } as AnalysisScan);
      });

      setScans(loadedScans);
    } catch (error) {
      console.error('Error loading scans:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteScan = async (scanId: string) => {
    if (!user) return;

    if (!confirm('Are you sure you want to delete this scan? This cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'analysisScans', scanId));
      alert('✅ Scan deleted successfully');
      loadScans();
    } catch (error) {
      console.error('Error deleting scan:', error);
      alert('❌ Failed to delete scan');
    }
  };

  const loadQueryResults = async (scanId: string) => {
    if (!user) return;

    try {
      const q = query(
        collection(db, 'analysisQueries'),
        where('scanId', '==', scanId),
        where('userId', '==', user.uid)
      );

      const querySnapshot = await getDocs(q);
      const results: any[] = [];

      querySnapshot.forEach((docSnap) => {
        results.push({ ...docSnap.data(), query_id: docSnap.id });
      });

      setQueryResults(results);
    } catch (error) {
      console.error('Error loading query results:', error);
      setQueryResults([]);
    }
  };

  const viewScanDetails = async (scan: AnalysisScan) => {
    setSelectedScan(scan);
    await loadQueryResults(scan.scan_id);
  };

  const createNewScan = async () => {
    if (!user || selectedPlatforms.length === 0) {
      alert('Please select at least one platform');
      return;
    }

    try {
      // Generate scan name
      const scanName = newScanName ||
        `${selectedTemplate !== 'custom'
          ? queryTemplates[selectedTemplate as keyof typeof queryTemplates].name
          : 'Custom'} - ${brandName || 'Scan'}`;

      // Create scan record
      const scanData: Partial<AnalysisScan> = {
        userId: user.uid,
        scan_date: Timestamp.now(),
        scan_type: 'full',
        scan_name: scanName,
        platforms_tested: selectedPlatforms,
        queries: [], // Will be populated as tasks complete
        total_queries: customQueries.filter((q) => q.trim()).length * selectedPlatforms.length,
        overall_score: 0,
        visibility_score: 0,
        accuracy_score: 0,
        sentiment_score: 0,
        gaps_identified: 0,
        critical_gaps: 0,
        high_priority_gaps: 0,
        medium_priority_gaps: 0,
        low_priority_gaps: 0,
        scan_status: 'in_progress',
        completion_percentage: 0,
        is_rescan: false,
        created_at: Timestamp.now(),
        updated_at: Timestamp.now(),
      };

      const scanRef = await addDoc(collection(db, 'analysisScans'), scanData);

      // Create scan tasks (one per platform per query)
      const tasks: Partial<ScanTask>[] = [];
      const queries = customQueries.filter((q) => q.trim());

      for (const platform of selectedPlatforms) {
        for (const queryText of queries) {
          tasks.push({
            scan_id: scanRef.id,
            userId: user.uid,
            platform,
            query_text: queryText,
            query_category: 'brand_awareness', // Default, will be classified
            status: 'queued',
            retry_count: 0,
            max_retries: 3,
            created_at: Timestamp.now(),
          });
        }
      }

      // Batch write tasks to Firestore
      const batch = writeBatch(db);
      const taskIds: string[] = [];

      for (const task of tasks) {
        const taskRef = doc(collection(db, 'analysisTasks'));
        taskIds.push(taskRef.id);
        batch.set(taskRef, task);
      }

      await batch.commit();

      // Show progress modal
      setScanProgress({
        show: true,
        current: 0,
        total: tasks.length,
        startTime: Date.now(),
      });

      // Trigger Cloud Function to process each task with rate limiting
      const scanFn = httpsCallable(functions, 'scanAIPlatform');
      const DELAY_BETWEEN_SCANS = 13000; // 13 seconds = ~4.6 requests/min (under Gemini 2.5 Flash 5/min limit)

      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        try {
          await scanFn({
            platform: task.platform,
            query: task.query_text,
            scanTaskId: taskIds[i],
            scanId: scanRef.id,
          });

          // Update progress
          setScanProgress(prev => ({ ...prev, current: i + 1 }));

          // Add delay before next scan (except for last one)
          if (i < tasks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_SCANS));
          }
        } catch (error) {
          console.error(`Failed to trigger scan for ${task.platform}:`, error);
        }
      }

      // Hide progress modal and reload scans
      setScanProgress({ show: false, current: 0, total: 0, startTime: 0 });
      alert(`✅ Scan completed! Processed ${tasks.length} queries.`);
      setShowNewScan(false);
      loadScans();
    } catch (error) {
      console.error('Error creating scan:', error);
      alert('❌ Failed to create scan');
    }
  };

  const [showFixesModal, setShowFixesModal] = useState(false);
  const [currentFixScanId, setCurrentFixScanId] = useState<string | null>(null);
  const [generatedFixes, setGeneratedFixes] = useState<any>(null);
  const [showCompanyInfoForm, setShowCompanyInfoForm] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    description: '',
    website: '',
    email: '',
    phone: '',
    location: '',
    industry: '',
  });

  const createWebsiteScan = async () => {
    if (!user || !websiteUrl.trim()) {
      alert('Please enter a valid website URL');
      return;
    }

    try {
      // Call the Cloud Function to scan the website
      const scanWebsiteFn = httpsCallable(functions, 'scanWebsiteCompliance');

      const scanId = `website_${Date.now()}`;

      const result = await scanWebsiteFn({
        url: websiteUrl,
        scanId,
      });

      const scanResult = result.data as any;

      alert(`✅ Website scan completed! AI Compliance Score: ${scanResult.overall_score}/100`);

      // Store scan ID for fix generation
      setCurrentFixScanId(scanResult.scan_id || scanId);

      setShowNewScan(false);
      setWebsiteUrl('');
      loadScans();

      // Prompt to generate fixes if score is below 70
      if (scanResult.overall_score < 70) {
        setTimeout(() => {
          if (confirm('Would you like to generate fix files for the identified issues?')) {
            setShowCompanyInfoForm(true);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error scanning website:', error);
      alert('❌ Failed to scan website. Please check the URL and try again.');
    }
  };

  const generateFixes = async () => {
    if (!user || !currentFixScanId) {
      alert('No scan selected');
      return;
    }

    try {
      const generateFixesFn = httpsCallable(functions, 'generateComplianceFixes');

      const result = await generateFixesFn({
        scanId: currentFixScanId,
        websiteUrl: websiteUrl || 'https://example.com',
        companyInfo,
      });

      const fixResult = result.data as any;
      setGeneratedFixes(fixResult);
      setShowCompanyInfoForm(false);
      setShowFixesModal(true);

      alert(`✅ Generated ${fixResult.fileCount} fix files!`);
    } catch (error) {
      console.error('Error generating fixes:', error);
      alert('❌ Failed to generate fix files');
    }
  };

  const downloadFixes = () => {
    if (!generatedFixes || !generatedFixes.files) return;

    // Create a zip-like download by downloading each file
    Object.entries(generatedFixes.files as Record<string, string>).forEach(([fileName, content]) => {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    });

    alert(`✅ Downloaded ${Object.keys(generatedFixes.files).length} files`);
  };

  const deployViaSftp = async () => {
    if (!user || !generatedFixes || !generatedFixes.fixId) {
      alert('No fixes to deploy');
      return;
    }

    if (!confirm('Deploy fixes to your website via SFTP? Make sure you have configured SFTP settings.')) {
      return;
    }

    try {
      const deployFn = httpsCallable(functions, 'deployComplianceFixesSftp');

      const result = await deployFn({
        fixId: generatedFixes.fixId,
      });

      const deployResult = result.data as any;

      alert(`✅ Deployed ${deployResult.filesUploaded}/${deployResult.totalFiles} files via SFTP!`);
      setShowFixesModal(false);
    } catch (error) {
      console.error('Error deploying fixes:', error);
      alert('❌ Failed to deploy via SFTP. Please check your SFTP settings.');
    }
  };

  const togglePlatform = (platform: AIPlatform) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const addQueryField = () => {
    setCustomQueries([...customQueries, '']);
  };

  const updateQuery = (index: number, value: string) => {
    const updated = [...customQueries];
    updated[index] = value;
    setCustomQueries(updated);
  };

  const removeQuery = (index: number) => {
    setCustomQueries(customQueries.filter((_, i) => i !== index));
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-50';
    if (score >= 60) return 'bg-yellow-50';
    return 'bg-red-50';
  };

  const getTrendIcon = (change?: number) => {
    if (!change || change === 0) return <Minus className="w-4 h-4 text-gray-400" />;
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-600" />;
    return <TrendingDown className="w-4 h-4 text-red-600" />;
  };

  const getFileDescription = (fileName: string): string => {
    if (fileName === 'llms.txt') return 'Site documentation for AI systems';
    if (fileName.startsWith('faq-')) return 'Structured FAQ content for AI';
    if (fileName.startsWith('schema-')) return 'JSON-LD structured data snippet';
    if (fileName === 'meta-tags.html') return 'Optimized HTML meta tags';
    if (fileName === 'robots.txt') return 'AI crawler permissions';
    if (fileName === 'sitemap.xml') return 'XML sitemap template';
    return 'AI compliance file';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t1-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analysis scans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <div className="bg-shell-navy border-b border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-gray-400 text-sm">Audit</span>
          <span className="text-gray-600">/</span>
          <span className="text-white text-sm font-medium">Analysis</span>
        </div>

        <div className="flex items-center gap-4">
          <span className="px-3 py-1 bg-orange-600 text-white text-xs font-semibold rounded uppercase">Growth</span>
          <button className="relative p-2 text-gray-400 hover:text-white">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-t1-primary uppercase tracking-wide mb-1">Kodiac Audit</p>
            <h1 className="text-3xl font-semibold text-gray-900">AI Platform Analysis</h1>
            <p className="text-sm text-gray-600 mt-1">
              Automated scanning and accuracy verification across AI platforms
            </p>
          </div>

          <Button onClick={() => setShowNewScan(true)}>
            <Play className="w-4 h-4" />
            New Scan
          </Button>
        </div>

      {/* Summary Cards */}
      {scans.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-card shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-label text-gray-600 uppercase">Overall Score</span>
              <BarChart3 className="w-4 h-4 text-gray-400" />
            </div>
            <div className="flex items-baseline gap-2">
              <span
                className={`text-score font-medium ${getScoreColor(scans[0].overall_score)}`}
              >
                {scans[0].overall_score}
              </span>
              <span className="text-body text-gray-500">/100</span>
              {scans[0].changes_since_previous && getTrendIcon(scans[0].changes_since_previous.score_change)}
            </div>
          </div>

          <div className="bg-white rounded-card shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-label text-gray-600 uppercase">Visibility</span>
              <Eye className="w-4 h-4 text-gray-400" />
            </div>
            <div className="flex items-baseline gap-2">
              <span
                className={`text-score font-medium ${getScoreColor(scans[0].visibility_score)}`}
              >
                {scans[0].visibility_score}
              </span>
              <span className="text-body text-gray-500">/100</span>
            </div>
          </div>

          <div className="bg-white rounded-card shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-label text-gray-600 uppercase">Accuracy</span>
              <CheckCircle className="w-4 h-4 text-gray-400" />
            </div>
            <div className="flex items-baseline gap-2">
              <span
                className={`text-score font-medium ${getScoreColor(scans[0].accuracy_score)}`}
              >
                {scans[0].accuracy_score}
              </span>
              <span className="text-body text-gray-500">/100</span>
            </div>
          </div>

          <div className="bg-white rounded-card shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-label text-gray-600 uppercase">Gaps Identified</span>
              <AlertTriangle className="w-4 h-4 text-gray-400" />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-score font-medium text-gray-900">
                {scans[0].gaps_identified}
              </span>
              <span className="text-caption text-red-600">{scans[0].critical_gaps} critical</span>
            </div>
          </div>
        </div>
      )}

      {/* Scan History */}
      <div className="bg-white rounded-card shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-heading text-gray-900">Scan History</h2>
        </div>

        {scans.length === 0 ? (
          <div className="text-center py-12">
            <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-body">No scans yet</p>
            <p className="text-caption text-gray-500 mt-1">
              Create your first scan to analyze how AI platforms represent your brand
            </p>
            <Button onClick={() => setShowNewScan(true)} className="mt-4">
              Create First Scan
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-label text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-label text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-label text-gray-700 uppercase">
                    Platforms
                  </th>
                  <th className="px-6 py-3 text-left text-label text-gray-700 uppercase">Queries</th>
                  <th className="px-6 py-3 text-left text-label text-gray-700 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-label text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-label text-gray-700 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {scans.map((scan) => (
                  <tr key={scan.scan_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-body text-gray-900">
                      {scan.scan_date.toDate().toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-body font-medium text-gray-900">
                        {(scan as any).scan_name || 'Unnamed Scan'}
                      </div>
                      <div className="text-caption text-gray-500">
                        {scan.total_queries} total queries
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-1 flex-wrap">
                        {scan.platforms_tested.map((platform) => (
                          <span
                            key={platform}
                            className="inline-flex items-center px-2 py-0.5 rounded text-caption bg-t1-light text-t1-primary capitalize"
                          >
                            {platform}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-body text-gray-900">
                      {scan.total_queries}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-caption font-medium ${getScoreBgColor(
                          scan.overall_score
                        )} ${getScoreColor(scan.overall_score)}`}
                      >
                        {scan.overall_score}/100
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {scan.scan_status === 'completed' && (
                        <div className="flex items-center gap-2 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-body">Complete</span>
                        </div>
                      )}
                      {scan.scan_status === 'in_progress' && (
                        <div className="flex items-center gap-2 text-yellow-600">
                          <Clock className="w-4 h-4" />
                          <span className="text-body">{scan.completion_percentage}%</span>
                        </div>
                      )}
                      {scan.scan_status === 'failed' && (
                        <div className="flex items-center gap-2 text-red-600">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-body">Failed</span>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => viewScanDetails(scan)}
                          className="text-t1-primary hover:text-t1-dark text-body font-medium"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => deleteScan(scan.scan_id)}
                          className="text-red-600 hover:text-red-800 text-body font-medium"
                          title="Delete scan"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Scan Modal */}
      {showNewScan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-display text-gray-900">Create New Analysis Scan</h2>
              <p className="text-body text-gray-600 mt-1">
                Configure platforms and queries to analyze
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Scan Type Selector */}
              <div>
                <label className="block text-body font-medium text-gray-700 mb-3">
                  Scan Type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setScanType('ai_platform')}
                    className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                      scanType === 'ai_platform'
                        ? 'border-t1-primary bg-t1-light'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          scanType === 'ai_platform'
                            ? 'border-t1-primary bg-t1-primary'
                            : 'border-gray-300'
                        }`}
                      >
                        {scanType === 'ai_platform' && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div>
                        <div className="text-body font-medium">AI Platform Scan</div>
                        <div className="text-caption text-gray-500">Test how AI platforms describe your brand</div>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setScanType('website_compliance')}
                    className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                      scanType === 'website_compliance'
                        ? 'border-t1-primary bg-t1-light'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          scanType === 'website_compliance'
                            ? 'border-t1-primary bg-t1-primary'
                            : 'border-gray-300'
                        }`}
                      >
                        {scanType === 'website_compliance' && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <div>
                        <div className="text-body font-medium">Website Compliance</div>
                        <div className="text-caption text-gray-500">Analyze how AI-ready your website is</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Scan Name */}
              <div>
                <label className="block text-body font-medium text-gray-700 mb-2">
                  Scan Name (Optional)
                </label>
                <input
                  type="text"
                  value={newScanName}
                  onChange={(e) => setNewScanName(e.target.value)}
                  placeholder="e.g., Weekly Brand Check"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                />
              </div>

              {/* AI Platform Scan Configuration */}
              {scanType === 'ai_platform' && (
                <>
                  {/* Brand Name */}
                  <div>
                    <label className="block text-body font-medium text-gray-700 mb-2">
                      Your Brand Name
                    </label>
                    <input
                      type="text"
                      value={brandName}
                      onChange={(e) => {
                        const newBrand = e.target.value;
                        setBrandName(newBrand);
                        // Auto-update queries with new brand name
                        if (selectedTemplate !== 'custom') {
                          applyTemplate(selectedTemplate, newBrand);
                        }
                      }}
                      placeholder="e.g., CloudSync, Acme Corp"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                    />
                    <p className="mt-1 text-caption text-gray-500">
                      Used to customize query templates
                    </p>
                  </div>

                  {/* Query Template Selector */}
                  <div>
                    <label className="block text-body font-medium text-gray-700 mb-3">
                      Query Template
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(queryTemplates).map(([key, template]) => (
                        <button
                          key={key}
                          onClick={() => applyTemplate(key)}
                          className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                            selectedTemplate === key
                              ? 'border-t1-primary bg-t1-light'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-start gap-2">
                            <div
                              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center mt-0.5 flex-shrink-0 ${
                                selectedTemplate === key
                                  ? 'border-t1-primary bg-t1-primary'
                                  : 'border-gray-300'
                              }`}
                            >
                              {selectedTemplate === key && (
                                <div className="w-2 h-2 bg-white rounded-full" />
                              )}
                            </div>
                            <div>
                              <div className="text-body font-medium">{template.name}</div>
                              <div className="text-caption text-gray-500">{template.description}</div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Platforms */}
                  <div>
                    <label className="block text-body font-medium text-gray-700 mb-3">
                      Select Platforms
                      {loadingPlatforms && (
                        <span className="ml-2 text-caption text-gray-500">(Loading...)</span>
                      )}
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      {(['chatgpt', 'claude', 'gemini', 'perplexity'] as AIPlatform[]).map(
                        (platform) => {
                          const isAvailable = availablePlatforms.includes(platform);
                          const isDisabled = !isAvailable;

                          return (
                            <button
                              key={platform}
                              onClick={() => isAvailable && togglePlatform(platform)}
                              disabled={isDisabled}
                              className={`px-4 py-3 rounded-lg border-2 text-left transition-all ${
                                isDisabled
                                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                                  : selectedPlatforms.includes(platform)
                                  ? 'border-t1-primary bg-t1-light'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              title={isDisabled ? 'API key not configured for this platform' : ''}
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                    selectedPlatforms.includes(platform)
                                      ? 'border-t1-primary bg-t1-primary'
                                      : 'border-gray-300'
                                  }`}
                                >
                                  {selectedPlatforms.includes(platform) && (
                                    <CheckCircle className="w-3 h-3 text-white" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <span className="text-body font-medium capitalize">{platform}</span>
                                  {!isAvailable && (
                                    <div className="text-caption text-gray-500">API key needed</div>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        }
                      )}
                    </div>

                    {availablePlatforms.length === 1 && availablePlatforms[0] === 'gemini' && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-caption text-yellow-900">
                          ℹ️ <strong>Only Gemini 2.5 Flash available.</strong> Configure OpenAI or Anthropic API keys in Firebase Functions to enable ChatGPT and Claude scanning.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Queries */}
                  <div>
                    <label className="block text-body font-medium text-gray-700 mb-3">
                      Test Queries
                      {selectedTemplate !== 'custom' && (
                        <span className="ml-2 text-caption text-gray-500">
                          ({customQueries.filter(q => q.trim()).length} queries from {queryTemplates[selectedTemplate as keyof typeof queryTemplates].name})
                        </span>
                      )}
                    </label>
                    <div className="space-y-3">
                      {customQueries.map((q, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={q}
                            onChange={(e) => {
                              updateQuery(index, e.target.value);
                              // Switch to custom template if user manually edits
                              if (selectedTemplate !== 'custom') {
                                setSelectedTemplate('custom');
                              }
                            }}
                            placeholder="e.g., What is [Your Brand]?"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                          />
                          {customQueries.length > 1 && (
                            <button
                              onClick={() => removeQuery(index)}
                              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={addQueryField}
                      className="mt-3 flex items-center gap-2 text-t1-primary hover:text-t1-dark text-body font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      Add Another Query
                    </button>
                  </div>

                  {/* Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-body text-blue-900 mb-2">
                      <strong>Scan Overview:</strong> This will create{' '}
                      {customQueries.filter((q) => q.trim()).length * selectedPlatforms.length} analysis
                      tasks ({customQueries.filter((q) => q.trim()).length} queries × {selectedPlatforms.length} platforms).
                    </p>
                    {selectedTemplate !== 'custom' && (
                      <p className="text-caption text-blue-800 mt-2">
                        ✨ Using <strong>{queryTemplates[selectedTemplate as keyof typeof queryTemplates].name}</strong> template.
                        You can edit queries or select a different template above.
                      </p>
                    )}
                    <p className="text-caption text-blue-800 mt-2">
                      💡 <strong>Tip:</strong> Enter your brand name above to auto-customize query templates with your brand.
                    </p>
                  </div>
                </>
              )}

              {/* Website Compliance Scan Configuration */}
              {scanType === 'website_compliance' && (
                <>
                  {/* Website URL */}
                  <div>
                    <label className="block text-body font-medium text-gray-700 mb-2">
                      Website URL
                    </label>
                    <input
                      type="url"
                      value={websiteUrl}
                      onChange={(e) => setWebsiteUrl(e.target.value)}
                      placeholder="https://example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                    />
                    <p className="mt-2 text-caption text-gray-500">
                      Enter the URL of the page you want to analyze for AI compliance
                    </p>
                  </div>

                  {/* What Will Be Analyzed */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="text-body font-semibold text-blue-900 mb-2">
                      10 Dimensions Analyzed:
                    </h3>
                    <ul className="space-y-1 text-caption text-blue-800">
                      <li>✓ Structured Data Presence (JSON-LD, Schema.org)</li>
                      <li>✓ Meta Tag Optimization (Title, Description, OpenGraph)</li>
                      <li>✓ Content Structure (Headings, FAQ, Tables)</li>
                      <li>✓ Crawler Accessibility (robots.txt, sitemap.xml)</li>
                      <li>✓ Semantic HTML (Article, Section tags)</li>
                      <li>✓ Technical SEO (HTTPS, Security headers)</li>
                      <li>✓ Content Quality (Readability, Depth)</li>
                      <li>✓ Knowledge Graph Readiness (Schema types)</li>
                      <li>✓ AI-Specific Features (FAQ schema, Contact info)</li>
                      <li>✓ Competitive Positioning (Benchmark comparison)</li>
                    </ul>
                  </div>
                </>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowNewScan(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={scanType === 'ai_platform' ? createNewScan : createWebsiteScan}
                className="px-4 py-2 bg-t1-primary text-white rounded-lg hover:bg-t1-dark transition-colors"
              >
                Start {scanType === 'ai_platform' ? 'AI Platform' : 'Website'} Scan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Info Form Modal */}
      {showCompanyInfoForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-display text-gray-900">Company Information</h2>
              <p className="text-body text-gray-600 mt-1">
                Provide your company details to generate personalized fix files
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-body font-medium text-gray-700 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={companyInfo.name}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                  placeholder="Your Company Inc."
                />
              </div>

              <div>
                <label className="block text-body font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={companyInfo.description}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                  placeholder="A leading provider of..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-body font-medium text-gray-700 mb-2">Website</label>
                  <input
                    type="url"
                    value={companyInfo.website}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, website: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                    placeholder="https://example.com"
                  />
                </div>

                <div>
                  <label className="block text-body font-medium text-gray-700 mb-2">Industry</label>
                  <input
                    type="text"
                    value={companyInfo.industry}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, industry: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                    placeholder="Technology, Healthcare, etc."
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-body font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={companyInfo.email}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                    placeholder="contact@example.com"
                  />
                </div>

                <div>
                  <label className="block text-body font-medium text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={companyInfo.phone}
                    onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                    placeholder="+1-555-0100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-body font-medium text-gray-700 mb-2">Location</label>
                <input
                  type="text"
                  value={companyInfo.location}
                  onChange={(e) => setCompanyInfo({ ...companyInfo, location: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-t1-primary focus:border-t1-primary text-body"
                  placeholder="San Francisco, CA"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setShowCompanyInfoForm(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={generateFixes}
                disabled={!companyInfo.name || !companyInfo.description}
                className="px-4 py-2 bg-t1-primary text-white rounded-lg hover:bg-t1-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Fix Files
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixes Modal */}
      {showFixesModal && generatedFixes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-display text-gray-900">AI Compliance Fix Files</h2>
              <p className="text-body text-gray-600 mt-1">
                {generatedFixes.fileCount} files generated to fix website AI compliance issues
              </p>
            </div>

            <div className="p-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="text-body font-semibold text-green-900 mb-2">
                  ✅ Fix Files Generated Successfully
                </h3>
                <p className="text-caption text-green-800">
                  These files will help improve your website's AI compliance score. You can download
                  them and upload manually, or deploy directly via SFTP.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-heading text-gray-900">Generated Files:</h3>

                <div className="space-y-2">
                  {generatedFixes.fileList?.map((fileName: string) => (
                    <div
                      key={fileName}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-t1-light rounded flex items-center justify-center">
                          <span className="text-t1-primary text-caption font-mono">
                            {fileName.split('.').pop()}
                          </span>
                        </div>
                        <div>
                          <div className="text-body font-medium text-gray-900">{fileName}</div>
                          <div className="text-caption text-gray-500">
                            {getFileDescription(fileName)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          const content = generatedFixes.files[fileName];
                          const blob = new Blob([content], { type: 'text/plain' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = fileName;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        }}
                        className="px-3 py-1 text-t1-primary hover:bg-t1-light rounded transition-colors text-caption"
                      >
                        Download
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-body font-semibold text-blue-900 mb-2">
                  Deployment Instructions:
                </h4>
                <ul className="space-y-1 text-caption text-blue-800">
                  <li>• <strong>llms.txt & robots.txt:</strong> Upload to website root directory</li>
                  <li>• <strong>FAQ files:</strong> Place in /ai/faqs/ directory</li>
                  <li>• <strong>Schema snippets:</strong> Add to your HTML <code>&lt;head&gt;</code> section</li>
                  <li>• <strong>Meta tags:</strong> Replace existing meta tags in <code>&lt;head&gt;</code></li>
                  <li>• <strong>sitemap.xml:</strong> Upload to root and add your site's pages</li>
                </ul>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
              <button
                onClick={() => setShowFixesModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Close
              </button>
              <div className="flex gap-3">
                <button
                  onClick={downloadFixes}
                  className="px-4 py-2 border border-t1-primary text-t1-primary rounded-lg hover:bg-t1-light transition-colors"
                >
                  Download All Files
                </button>
                <button
                  onClick={deployViaSftp}
                  className="px-4 py-2 bg-t1-primary text-white rounded-lg hover:bg-t1-dark transition-colors"
                >
                  Deploy via SFTP
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TODO: Scan Detail View */}
      {selectedScan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-card max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-display text-gray-900">
                    Scan Results - {selectedScan.scan_date.toDate().toLocaleDateString()}
                  </h2>
                  <p className="text-body text-gray-600 mt-1">
                    {selectedScan.total_queries} queries across {selectedScan.platforms_tested.length}{' '}
                    platforms
                  </p>
                </div>
                <button
                  onClick={() => setSelectedScan(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Score Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-body-small text-gray-600 mb-1">Overall</div>
                  <div className={`text-h2 font-bold ${getScoreColor(selectedScan.overall_score)}`}>
                    {selectedScan.overall_score}/100
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-body-small text-gray-600 mb-1">Visibility</div>
                  <div className={`text-h2 font-bold ${getScoreColor(selectedScan.visibility_score)}`}>
                    {selectedScan.visibility_score}/100
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-body-small text-gray-600 mb-1">Sentiment</div>
                  <div className={`text-h2 font-bold ${getScoreColor(selectedScan.sentiment_score)}`}>
                    {selectedScan.sentiment_score}/100
                  </div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-body-small text-gray-600 mb-1">Status</div>
                  <div className="text-body font-semibold text-gray-900">
                    {selectedScan.completion_percentage}% Complete
                  </div>
                </div>
              </div>

              {/* Query Results */}
              <h3 className="text-h3 font-bold text-gray-900 mb-4">Query Results</h3>

              {queryResults.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No query results found. The scan may still be processing.
                </div>
              ) : (
                <div className="space-y-4">
                  {queryResults.map((result, index) => (
                    <div key={result.query_id} className="border border-gray-200 rounded-lg p-4">
                      {/* Query Header */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-body-small font-semibold text-gray-500">
                              Query {index + 1}
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-body-small rounded">
                              {result.platform}
                            </span>
                          </div>
                          <div className="text-body font-medium text-gray-900">
                            {result.query_text}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {result.brand_mentioned ? (
                            <span className="px-3 py-1 bg-green-100 text-green-800 text-body-small rounded-full font-medium">
                              ✓ Brand Mentioned
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-red-100 text-red-800 text-body-small rounded-full font-medium">
                              ✗ Not Mentioned
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Metrics */}
                      <div className="flex items-center gap-4 mb-3 text-body-small text-gray-600">
                        <div>
                          <strong>Sentiment:</strong> {result.sentiment_score}/100
                        </div>
                        {result.position_in_response >= 0 && (
                          <div>
                            <strong>Position:</strong> Character {result.position_in_response}
                          </div>
                        )}
                      </div>

                      {/* Response Preview */}
                      <div className="mt-3 p-3 bg-gray-50 rounded border border-gray-200">
                        <div className="text-body-small font-semibold text-gray-700 mb-2">
                          AI Response:
                        </div>
                        <div className="text-body-small text-gray-600 line-clamp-3">
                          {result.response_text}
                        </div>
                        <button
                          onClick={() => {
                            alert(result.response_text);
                          }}
                          className="mt-2 text-body-small text-t1-primary hover:text-t1-dark font-medium"
                        >
                          Read Full Response →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Info about scores */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="text-body font-semibold text-blue-900 mb-2">Score Breakdown:</h4>
                <ul className="text-body-small text-blue-800 space-y-1">
                  <li><strong>Visibility Score:</strong> Percentage of queries where your brand was mentioned</li>
                  <li><strong>Sentiment Score:</strong> Average sentiment across all responses (50 = neutral)</li>
                  <li><strong>Overall Score:</strong> Average of visibility + sentiment scores</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scan Progress Modal */}
      {scanProgress.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
            <h3 className="text-h3 font-bold text-gray-900 mb-4">Scan in Progress</h3>

            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between text-body text-gray-700 mb-2">
                <span>Processing queries...</span>
                <span>{scanProgress.current}/{scanProgress.total}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-primary-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(scanProgress.current / scanProgress.total) * 100}%` }}
                />
              </div>
            </div>

            {/* Timer */}
            <div className="text-center mb-6">
              <div className="text-body text-gray-600">Elapsed Time</div>
              <div className="text-h2 font-bold text-gray-900 mt-1">
                {(() => {
                  const elapsed = Math.floor((Date.now() - scanProgress.startTime) / 1000);
                  const minutes = Math.floor(elapsed / 60);
                  const seconds = elapsed % 60;
                  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                })()}
              </div>
            </div>

            {/* Estimated Time Remaining */}
            <div className="text-center text-body text-gray-600">
              {scanProgress.current > 0 && scanProgress.current < scanProgress.total && (
                <>
                  <div className="mb-2">Estimated time remaining</div>
                  <div className="text-h3 font-semibold text-gray-700">
                    {(() => {
                      const elapsed = (Date.now() - scanProgress.startTime) / 1000;
                      const avgTimePerQuery = elapsed / scanProgress.current;
                      const remaining = Math.ceil(avgTimePerQuery * (scanProgress.total - scanProgress.current));
                      const minutes = Math.floor(remaining / 60);
                      const seconds = remaining % 60;
                      return `~${minutes}:${seconds.toString().padStart(2, '0')}`;
                    })()}
                  </div>
                </>
              )}
            </div>

            {/* Info Message */}
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-body-small text-blue-900">
                <strong>Note:</strong> We're rate limiting queries to stay within API limits (5 per minute).
                Gemini free tier has a low quota - consider adding ChatGPT or Claude API keys for faster scanning.
                This ensures reliable results without errors.
              </p>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
